import type { JSX } from "react";

import { useTranslation } from "@/i18n";
import { cn } from "@/lib/cn";

/**
 * One placeholder block. The sheen sweeps across it, and reduced motion keeps only a
 * slow pulse: a still placeholder reads as broken furniture, a moving one as loading.
 */
export function SkeletonBlock({ className }: { className?: string | undefined }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative block overflow-hidden rounded-small bg-skeleton after:absolute after:inset-0 after:-translate-x-full after:bg-[linear-gradient(90deg,transparent,var(--k-skeleton-sheen),transparent)] after:[animation:k-sweep_var(--k-skeleton-speed)_ease-in-out_infinite] motion-reduce:after:hidden motion-reduce:[animation:k-pulse_var(--k-skeleton-speed)_ease-in-out_infinite]",
        className,
      )}
    />
  );
}

/**
 * Shapes a screen declares while it waits for data. Declared per call site because a
 * component cannot know the shape of children it has not rendered yet, and the layout
 * must not jump when the data lands.
 */
export type SkeletonSpec =
  | { shape: "text"; rows?: number | undefined }
  | { shape: "list"; rows?: number | undefined }
  | { shape: "table"; columns: number; rows?: number | undefined }
  | { shape: "cards"; count?: number | undefined }
  | { shape: "stats"; count?: number | undefined }
  | { shape: "board"; columns?: number | undefined }
  | { shape: "chart" }
  | { shape: "detail" };

/** Widths cycle so neighbouring bars differ the way real text does. Never random. */
const WIDTHS = ["w-4/5", "w-3/5", "w-11/12", "w-2/3", "w-3/4", "w-1/2"] as const;

function width(index: number): string {
  return WIDTHS[index % WIDTHS.length] ?? "w-2/3";
}

const CHART_HEIGHTS = ["h-[46%]", "h-[68%]", "h-[34%]", "h-[82%]", "h-[58%]", "h-[74%]", "h-[40%]", "h-[90%]", "h-[52%]", "h-[66%]", "h-[38%]", "h-[78%]"] as const;

function range(count: number): readonly number[] {
  return Array.from({ length: count }, (_, index) => index);
}

function Shape({ spec }: { spec: SkeletonSpec }): JSX.Element {
  switch (spec.shape) {
    case "text":
      return (
        <div className="flex flex-col gap-2">
          {range(spec.rows ?? 3).map((index) => (
            <SkeletonBlock key={index} className={cn("h-3.5", width(index))} />
          ))}
        </div>
      );
    case "list":
      return (
        <div className="flex flex-col">
          {range(spec.rows ?? 4).map((index) => (
            <div key={index} className="flex items-center gap-3 border-b border-line py-2.5">
              <SkeletonBlock className="size-7 shrink-0 rounded-pill" />
              <div className="flex flex-1 flex-col gap-1.5">
                <SkeletonBlock className={cn("h-3.5", width(index))} />
                <SkeletonBlock className={cn("h-3", width(index + 3))} />
              </div>
            </div>
          ))}
        </div>
      );
    case "table": {
      const template = { gridTemplateColumns: `repeat(${String(spec.columns)}, minmax(0, 1fr))` };
      return (
        <div className="flex flex-col">
          <div className="grid gap-3 border-b border-line py-2" style={template}>
            {range(spec.columns).map((index) => (
              <SkeletonBlock key={index} className={cn("h-2.5", width(index + 4))} />
            ))}
          </div>
          {range(spec.rows ?? 6).map((row) => (
            <div key={row} className="grid h-10 items-center gap-3 border-b border-line" style={template}>
              {range(spec.columns).map((column) => (
                <SkeletonBlock key={column} className={cn("h-3", width(row + column))} />
              ))}
            </div>
          ))}
        </div>
      );
    }
    case "cards":
      return (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {range(spec.count ?? 3).map((index) => (
            <div key={index} className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3">
              <SkeletonBlock className={cn("h-3.5", width(index))} />
              <SkeletonBlock className={cn("h-3", width(index + 1))} />
              <SkeletonBlock className={cn("h-3", width(index + 2))} />
            </div>
          ))}
        </div>
      );
    case "stats":
      return (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {range(spec.count ?? 4).map((index) => (
            <div key={index} className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3">
              <SkeletonBlock className="h-2.5 w-1/2" />
              <SkeletonBlock className="h-6 w-3/4" />
            </div>
          ))}
        </div>
      );
    case "board":
      return (
        <div className="flex gap-3 overflow-hidden">
          {range(spec.columns ?? 5).map((column) => (
            <div key={column} className="flex w-60 shrink-0 flex-col gap-2">
              <SkeletonBlock className={cn("h-2.5", width(column))} />
              {range(2 + (column % 2)).map((card) => (
                <div key={card} className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3">
                  <SkeletonBlock className={cn("h-3.5", width(column + card))} />
                  <SkeletonBlock className={cn("h-3", width(card + 2))} />
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    case "chart":
      return (
        <div className="flex flex-col gap-3 rounded-card border border-line bg-surface p-3">
          <SkeletonBlock className="h-3.5 w-1/3" />
          <div className="flex h-48 items-end gap-2">
            {CHART_HEIGHTS.map((height, index) => (
              <SkeletonBlock key={index} className={cn("flex-1", height)} />
            ))}
          </div>
        </div>
      );
    case "detail":
      return (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3">
            <SkeletonBlock className="h-6 w-2/5" />
            <SkeletonBlock className="h-3 w-3/5" />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {range(2).map((card) => (
              <div key={card} className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3">
                <SkeletonBlock className={cn("h-3.5", width(card))} />
                <SkeletonBlock className={cn("h-3", width(card + 1))} />
                <SkeletonBlock className={cn("h-3", width(card + 2))} />
              </div>
            ))}
          </div>
        </div>
      );
  }
}

/**
 * A content placeholder, announced once. A list of shapes stacks them, which is how a
 * panel that loads a stat row, a chart and a table behind one query gets a placeholder
 * that looks like that panel.
 */
export function Skeleton({ spec }: { spec: SkeletonSpec | readonly SkeletonSpec[] }): JSX.Element {
  const { t } = useTranslation();
  const specs: readonly SkeletonSpec[] = Array.isArray(spec) ? (spec as readonly SkeletonSpec[]) : [spec as SkeletonSpec];
  return (
    <div role="status" aria-busy="true" className="flex flex-col gap-4">
      <span className="sr-only">{t("common.loading")}</span>
      {specs.map((one, index) => (
        <Shape key={`${one.shape}-${String(index)}`} spec={one} />
      ))}
    </div>
  );
}
